import axios from 'axios'
import { UAParser } from 'ua-parser-js'

import { canUseDom, canUseNavigator } from './utils/canUse'
import { AuthenticationSdk } from './authentication'
import { Configuration, type MetadataContent, type TrackingRequest, type TrackingResponseSuccess } from './generated'
import { addAxiosRequestInterceptors } from './interceptors'
import { type StorageSdk } from './storage'
import {
  ClickEventId,
  getMarketLanguage,
  getTrackingDeviceType,
  getTrackingOs,
  PageName,
  SearchUsage,
  TrackingSdk,
} from './tracking'
import { DeviceSettings, User, UserSdk } from './user'

export * from './authentication'
export * from './error'
export * from './generated/configuration'
export * from './generated/model'
export * from './interceptors'
export * from './storage'
export * from './tracking'
export * from './user'
export * from './userConstants'

export type MiniSdk = {
  trackingSdk: TrackingSdk
  storageSdk: StorageSdk
  trackingEvent?: {
    ClickEventId: {
      rechercheAutoCompletion: string
      rechercheLibre: string
    }
    SearchUsageEvent: {
      rechercheLibre: string
      rechercheAutoCompletion: string
      rechercheAutoCompletionSuggestion: string
      rechercheAutoCompletionHistorique: string
    }
    PageName: {
      Homepage: string
    }
  }
}

declare let ENV: string

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    ivtsSdk: MiniSdk
    ivtsTag: {
      config: {
        clientId: string
        market: string
        bff: string | undefined
      }
      data: TrackingRequest
    }
    utag_data: Record<string, string | string[]> | undefined
    utag: {
      view: (datalayer: MetadataContent | undefined) => void
    }
    Didomi: {
      getUserStatus(): {
        consent_string: string
        vendors: {
          consent: {
            enabled: string[]
          }
        }
      }
      getUserConsentStatusForVendor(vendor: string): boolean
    }
    didomiEventListeners: {
      event: string
      listener: () => void
    }[]
    didomiOnReady: (() => void)[]
  }
}

export const initMiniSdk = (configuration: Configuration, userSdk: UserSdk): MiniSdk => {
  const axiosInstance = axios.create()

  const trackingSdk = new TrackingSdk(configuration, userSdk, axiosInstance)
  const authenticationSdk = new AuthenticationSdk(trackingSdk, configuration, userSdk, axiosInstance)
  addAxiosRequestInterceptors(axiosInstance, configuration, userSdk, authenticationSdk)

  return {
    trackingSdk,
    storageSdk: userSdk.storageSdk,
    trackingEvent: {
      ClickEventId: {
        rechercheAutoCompletion: ClickEventId.RechercheAutoCompletion.toString(),
        rechercheLibre: ClickEventId.RechercheLibre.toString(),
      },
      SearchUsageEvent: {
        rechercheLibre: SearchUsage.LIBRE.toString(),
        rechercheAutoCompletion: SearchUsage.AUTOCOMPLETION.toString(),
        rechercheAutoCompletionSuggestion: SearchUsage.SUGGESTION.toString(),
        rechercheAutoCompletionHistorique: SearchUsage.HISTORIQUE.toString(),
      },
      PageName: {
        Homepage: PageName.Homepage.toString(),
      },
    },
  }
}

export const initBffSdkLibrary = async (): Promise<MiniSdk> => {
  const param = { basePath: '/bff' }
  const configuration = new Configuration(param)
  let osVersion = ''
  let deviceType = ''

  if (canUseNavigator()) {
    const uaParser = new UAParser(window.navigator.userAgent)
    const os = uaParser.getOS()
    osVersion = getTrackingOs(os.name, os.version)
    deviceType = getTrackingDeviceType(uaParser.getDevice().type)
  }
  const userSdk = new UserSdk(
    new User(
      new DeviceSettings(
        window.ivtsTag?.config?.market || getMarketLanguage(),
        'web',
        window.ivtsTag?.config?.clientId,
        ENV,
        osVersion,
        deviceType,
      ),
    ),
  )

  return initMiniSdk(configuration, userSdk)
}

const filterAsync = async <Item>(
  array: Item[],
  callbackFn: (value: Item, index: number, array: Item[]) => Promise<boolean>,
): Promise<Item[]> => {
  const filterMap = await Promise.all(array.map(callbackFn))

  return array.filter((_value, index) => filterMap[index])
}

export const initDidomi = (trackingSdk: TrackingSdk): void => {
  window.didomiEventListeners = window.didomiEventListeners || []
  window.didomiEventListeners.push({
    event: 'consent.changed',
    async listener() {
      const consentString = window.Didomi?.getUserStatus()?.consent_string
      const vendorsWithoutPurposes = window.Didomi?.getUserStatus()?.vendors.consent.enabled
      const vendorsWithPurposes =
        (await filterAsync(vendorsWithoutPurposes, async (vendor: string) =>
          window.Didomi?.getUserConsentStatusForVendor(vendor),
        )) || []
      await trackingSdk.updateConsentData(vendorsWithPurposes, consentString)
    },
  })
  window.didomiOnReady = window.didomiOnReady || []
  window.didomiOnReady.push(async function updateMissingConsentData() {
    const vendorsWithoutPurposes = window.Didomi?.getUserStatus()?.vendors.consent.enabled
    const vendorsWithPurposes =
      (await filterAsync(
        vendorsWithoutPurposes,
        async (vendor: string) => window.Didomi?.getUserConsentStatusForVendor(vendor) === true,
      )) || []
    await trackingSdk.updateMissingConsentData(vendorsWithPurposes)
  })
}

initBffSdkLibrary().then((sdk) => {
  if (canUseDom()) {
    window.ivtsSdk = sdk // expose sdk for external partners
    sdk.trackingSdk.init()
    initDidomi(sdk.trackingSdk)
    document.addEventListener('click', (evt) => sdk.trackingSdk.updateClickPosition(evt), true)
    sdk.trackingSdk.trackExternalPage(window.ivtsTag?.data).then((trackingInfo: TrackingResponseSuccess): void => {
      sdk.trackingSdk.updatePreviousPageName(trackingInfo.datalayer?.stringValues?.page_name)

      let timeout = 10

      const poll = () => {
        setTimeout(() => {
          timeout--

          if (window.utag && window.utag.view) {
            window.utag_data = trackingInfo.datalayer?.stringValues || {}
            window.utag.view(window.utag_data)
          } else if (timeout > 0) {
            poll()
          } else {
            // eslint-disable-next-line no-console
            console.error('Tealium is not loaded')
          }
        }, 500)
      }
      poll()
    })
  }
})
